import React, { useEffect, useState, useRef } from "react";
import type { WordRowModel } from '../../type/word';
import type { UserParamsMap } from "../../type/user";
import { moveToIcon, cancelIcon, okIcon } from "../../utils/config";
import ConfirmDialog from "../../modal/confirmDialog/confirmDialog";
import SpeakButton from "../../components/speakButton/speakButton";
import ExampleAiButton from "../../components/exampleAiButton/exampleAiButton";
import "./wordRow.scss";
import EditButton from "../../components/editButton/editButton";
import DeleteButton from "../../components/deleteButton/deleteButton";

const WordRow: React.FC<
  WordRowModel & 
  { parameters?: UserParamsMap; selectMode?: boolean; isSelected?: boolean; onToggleSelect?: () => void }
  > = ({ word, index, updateWord, deleteWord, dictionaries, moveWord, isMobile, parameters, selectedWordId, selectWord, selectMode = false, isSelected = false, onToggleSelect, langCode, langName }) => {
  
  const [isEditing, setIsEditing] = useState(false);
  const [eng, setEng] = useState(word.eng);
  const [pl, setPl] = useState(word.pl);
  const [menuOpen, setMenuOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);


  useEffect(() => {
    if (menuOpen && !isMobile && rowRef.current && menuRef.current) {
      const container = rowRef.current.closest(".table-container");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const rowRect = rowRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();

      if (rowRect.bottom + menuRect.height > containerRect.bottom) {
        menuRef.current.classList.add("words-menu-above");
      } else {
        menuRef.current.classList.remove("words-menu-above");
      }
    }
  }, [menuOpen, isMobile]);

  const handleSave = () => {
    updateWord(word.idexpression!, eng, pl);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEng(word.eng);
    setPl(word.pl);
    setIsEditing(false);
  };

  const handleDeleteClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmDialog(false);
    await deleteWord(word.idexpression!);
  };    

  return (
    <>
      <div
        className={`words-item ${isEditing ? 'editing' : ''} ${selectedWordId === word.idexpression ? 'selected' : ''} ${menuOpen ? 'menu-open' : ''} ${selectMode ? 'select-mode' : ''}`}
        ref={rowRef}
        onClick={() => selectWord(word.idexpression!)}
      >

        {isMobile ? (
          // Mobile Card Layout
          <>
            <div className="words-card-content">
              <div className="words-eng-cell">
                <div className="words-cell-content">
                  {isEditing ? (
                    <input
                      type="text"
                      className="form-control"
                      value={eng}
                      onChange={e => setEng(e.target.value)}
                      autoFocus
                    />
                  ) : (
                    <span className="words-eng-text addFontSize-2rem">{eng}</span>
                  )}
                </div>
              </div>

              <div className="words-divider" />

              <div className="words-pl-cell">
                <div className="words-cell-content">
                  {isEditing ? (
                    <input
                      type="text"
                      className="form-control"
                      value={pl}
                      onChange={e => setPl(e.target.value)}
                    />
                  ) : (
                    <span className="words-pl-text addFontSize-1_5rem">{pl}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="words-card-footer">
              <div className="words-index-number">{index + 1}</div>
              <div className="words-actions">
                {isEditing ? (
                  <>
                    <button className="words-action-btn words-action-btn--save" onClick={handleSave} title="Save">
                      {okIcon}
                    </button>
                    <button className="words-action-btn words-action-btn--cancel" onClick={handleCancel} title="Cancel">
                      {cancelIcon}
                    </button>
                  </>
                ) : (
                  <>
                    {parameters?.editbuttons_visible?.value && (
                      <>
                        <button
                          className="words-action-btn words-action-btn--move"
                          onClick={() => setMenuOpen(true)}
                          title="Move to dictionary"
                        >
                          {moveToIcon}
                        </button>
                        <EditButton
                          className="words-action-btn words-action-btn--edit"
                          onClick={() => setIsEditing(true)}
                        />
                        <DeleteButton
                          className="words-action-btn words-action-btn--delete"
                          onClick={handleDeleteClick}
                        />
                      </>
                    )}

                    <ExampleAiButton text={word.eng} langName={langName} langCode={langCode} />
                    <div>
                    <SpeakButton text={word.eng} lang={langCode} />
</div>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          // Desktop Grid Layout (unchanged)
          <>
            {selectMode && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleSelect?.();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }}
                />
              </div>
            )}
            <div>{index + 1}</div>
            <div className="words-eng-cell">
              {isEditing ? (
                <input
                  type="text"
                  className="form-control words-form-control-sm"
                  value={eng}
                  onChange={e => setEng(e.target.value)}
                />
              ) : (
                <>
                  <strong>{eng} <SpeakButton text={word.eng} lang={langCode} /> <ExampleAiButton text={word.eng} langName={langName} langCode={langCode} /></strong>
                </>
              )}
            </div>
            <div className="words-pl-cell">
              {isEditing ? (
                <input
                  type="text"
                  className="form-control"
                  value={pl}
                  onChange={e => setPl(e.target.value)}
                />
              ) : (
                pl
              )}
            </div>
            <div className="words-actions">
              {isEditing ? (
                <>
                  <button className="text-success" onClick={handleSave} title="Save">
                    {okIcon}
                  </button>
                  <button className="text-secondary" onClick={handleCancel} title="Cancel">
                    {cancelIcon}
                  </button>
                </>
              ) : (
                <>
                  {parameters?.editbuttons_visible?.value && selectMode && (
                    <>
                      <EditButton
                        className="words-action-btn words-action-btn--edit"
                        onClick={() => setIsEditing(true)}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Mobile menu - bottom sheet */}
      {menuOpen && isMobile && (
        <>
          <div className="words-menu-overlay" onClick={() => setMenuOpen(false)} />
          <div
            role="menu"
            className="words-move-menu words-move-menu--bottom-sheet shadow-sm"
          >
            <div className="words-mobile-menu-handle" />
            <div className="words-mobile-menu-title-container">
              <div className="words-mobile-menu-title-text">
                Move to Dictionary
              </div>
              <button onClick={() => setMenuOpen(false)}>
                  ×
              </button>
            </div>
            <div className="words-current-dictionary">{okIcon} {dictionaries.find(d => d.iddictionary === (word.iddictionary ?? undefined))?.name}</div>
            {dictionaries.length === 0 ? (
              <div className="words-no-dictionaries">No dictionaries</div>
            ) : (
              dictionaries
                .filter(d => d.iddictionary !== (word.iddictionary ?? undefined))
                .map(d => (
                  <button
                    key={d.iddictionary}
                    className="words-menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      moveWord(word.idexpression!, d.iddictionary);
                    }}
                  >
                    {d.name}
                  </button>
                ))
            )}
          </div>
        </>
        
      )}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        message="You sure you wanna delete this expression?"
        title="Confirm Deletion"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowConfirmDialog(false)}
      />   
    </>
    
  );
};

export default WordRow;